/** Shared date-range helpers for Senior Center reports. */

export type ReportDayPreset = 'today' | 'tomorrow' | 'day_after';

export type ReportTimeframe = ReportDayPreset | 'weekly' | 'monthly' | 'yearly' | 'custom';

export type ReportMetricFilter = 'reserved' | 'received' | 'both';

export function isReportDayPreset(timeframe: ReportTimeframe): timeframe is ReportDayPreset {
  return timeframe === 'today' || timeframe === 'tomorrow' || timeframe === 'day_after';
}

/** Sunday-start local week containing `date` (BakerRipley / US ops). */
export function getReportWeekBounds(date: Date = new Date()): { fromDate: string; toDate: string } {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = local.getDay(); // 0 = Sunday
  const start = addReportCalendarDays(local, -day);
  const end = addReportCalendarDays(start, 6);
  return {
    fromDate: toReportDateInputValue(start),
    toDate: toReportDateInputValue(end),
  };
}

/** Shift a week range by `weekOffset` weeks (negative = past). */
export function shiftReportWeekRange(
  fromDate: string,
  weekOffset: number,
): { fromDate: string; toDate: string } {
  const start = new Date(`${fromDate}T00:00:00`);
  const shifted = addReportCalendarDays(start, weekOffset * 7);
  return getReportWeekBounds(shifted);
}

/** Label for Figma week control: "This Week" or "Aug 01 - Aug 07". */
export function formatReportWeekLabel(fromDate: string, toDate: string, now: Date = new Date()): string {
  const current = getReportWeekBounds(now);
  if (fromDate === current.fromDate && toDate === current.toDate) {
    return 'This Week';
  }
  const format = (value: string) => {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${month} ${day}`;
  };
  return `${format(fromDate)} - ${format(toDate)}`;
}

export function toReportDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Local calendar day offset (avoids UTC shift from toISOString). */
export function addReportCalendarDays(base: Date, days: number): Date {
  const next = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

export function buildReportYearOptions(currentYear: number, yearsBack = 2): string[] {
  // Current year + previous yearsBack → last (yearsBack + 1) years (e.g. 3 total).
  const years: string[] = [];
  for (let year = currentYear; year >= currentYear - yearsBack; year -= 1) {
    years.push(String(year));
  }
  return years;
}

export function buildReportDownloadSuffix(options: {
  timeframe: ReportTimeframe;
  selectedYear: string;
  selectedMonthNumber: string;
  customFromDate: string;
  customToDate: string;
}): string {
  if (options.timeframe === 'today') {
    return 'today';
  }
  if (options.timeframe === 'tomorrow') {
    return 'tomorrow';
  }
  if (options.timeframe === 'day_after') {
    return 'day-after';
  }
  if (options.timeframe === 'weekly') {
    return `week_${options.customFromDate}_to_${options.customToDate}`;
  }
  if (options.timeframe === 'custom') {
    return `${options.customFromDate}_to_${options.customToDate}`;
  }
  if (options.timeframe === 'yearly') {
    return options.selectedYear || 'year';
  }
  return `${options.selectedYear}-${options.selectedMonthNumber}` || 'month';
}

export function resolveReportDateRange(options: {
  timeframe: ReportTimeframe;
  selectedYear: string;
  selectedMonthNumber: string;
  customFromDate: string;
  customToDate: string;
  /** Optional clock for presets; defaults to now (local). */
  now?: Date;
}): { fromDate: string; toDate: string } | null {
  const pad = (n: number) => String(n).padStart(2, '0');

  if (isReportDayPreset(options.timeframe)) {
    const offset = options.timeframe === 'today' ? 0 : options.timeframe === 'tomorrow' ? 1 : 2;
    const day = toReportDateInputValue(addReportCalendarDays(options.now ?? new Date(), offset));
    return { fromDate: day, toDate: day };
  }

  if (options.timeframe === 'weekly' || options.timeframe === 'custom') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(options.customFromDate) || !/^\d{4}-\d{2}-\d{2}$/.test(options.customToDate)) {
      return null;
    }
    if (options.customToDate < options.customFromDate) {
      return null;
    }
    return { fromDate: options.customFromDate, toDate: options.customToDate };
  }

  if (!/^\d{4}$/.test(options.selectedYear)) {
    return null;
  }
  const year = Number(options.selectedYear);
  if (!year || year < 2000 || year > 2100) {
    return null;
  }

  if (options.timeframe === 'yearly') {
    return {
      fromDate: `${year}-01-01`,
      toDate: `${year}-12-31`,
    };
  }

  if (!/^\d{2}$/.test(options.selectedMonthNumber)) {
    return null;
  }
  const month = Number(options.selectedMonthNumber);
  if (!month || month < 1 || month > 12) {
    return null;
  }
  const lastDay = new Date(year, month, 0).getDate();
  return {
    fromDate: `${year}-${pad(month)}-01`,
    toDate: `${year}-${pad(month)}-${pad(lastDay)}`,
  };
}

export function formatReportDateDisplay(value: string | Date | null | undefined): string {
  if (!value) {
    return '—';
  }
  const raw = typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10);
  const date = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${month} ${day} ${year}`;
}

/** Toolbar date label, e.g. "Aug 08 2026". Empty → placeholder. */
export function formatReportDatePickerLabel(value: string | Date | null | undefined): string {
  if (!value) {
    return 'Select date';
  }
  const raw = typeof value === 'string' ? value.slice(0, 10) : toReportDateInputValue(value);
  const date = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${month} ${day} ${year}`;
}

export const REPORT_MONTH_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

/**
 * Opens the native date picker for styled/invisible `type="date"` overlays.
 * Decorative chevrons do not open Chrome's calendar without `showPicker()`.
 */
export function openNativeDatePicker(event: Event): void {
  const current = event.currentTarget as HTMLElement | null;
  let input: HTMLInputElement | null = null;
  if (current instanceof HTMLInputElement && current.type === 'date') {
    input = current;
  } else if (current) {
    input = current.querySelector('input[type="date"]');
  }
  if (!input) {
    return;
  }

  try {
    input.focus({ preventScroll: true });
    const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
    if (typeof picker === 'function') {
      picker.call(input);
    }
  } catch {
    // showPicker can throw if not triggered by a trusted user gesture or unsupported.
  }
}

/**
 * Format meal report cell text:
 * - Ordered (reserved): whole number only, e.g. "204"
 * - Delivered (received): whole number only, e.g. "90"
 * - both: delivered/reserved ratio, e.g. "90/137" (legacy; meal report uses single counts)
 */
export function formatReservedReceivedCell(
  ordered: number,
  delivered: number,
  metric: ReportMetricFilter,
): string {
  const reserved = ordered ?? 0;
  const received = delivered ?? 0;
  if (metric === 'reserved') {
    return String(reserved);
  }
  if (metric === 'received') {
    return String(received);
  }
  return `${received}/${reserved}`;
}
